import styles from './UploadSout.module.css';
import Button from '../../Button/Button';
import { uploadSoutUrl } from '../../../helpers/constants';
import React, { useRef, useState } from 'react';
import useApi from '../../../hooks/useApi.hook';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import Spinner from '../../Spinner/Spinner';

export function UploadSout({ setModalOpen, setRefreshKey }) {
	const api = useApi();
	const fileInputRef = useRef(null);

	const [file, setFile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(undefined);

	const [phase, setPhase] = useState('upload'); // 'upload' | 'confirm'
	const [draft, setDraft] = useState(null);

	const [confirmLoading, setConfirmLoading] = useState(false);
	const [confirmResult, setConfirmResult] = useState(null); // null | 'confirmed' | 'rejected'

	const downloadBlob = (blob, filename) => {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const handleUpload = async () => {
		if (!file) return;
		setLoading(true);
		setError(undefined);
		try {
			const formData = new FormData();
			formData.append('file', file);

			const { data } = await api.post(uploadSoutUrl, formData, {
				headers: { 'Content-Type': 'multipart/form-data' }
			});

			const previewResp = await api.get(
				`${uploadSoutUrl}/${data.draft_id}/preview`,
				{ responseType: 'blob' }
			);
			downloadBlob(previewResp.data, `sout_import_${data.draft_id}.xlsx`);

			setDraft(data);
			setPhase('confirm');
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setLoading(false);
		}
	};

	const showResultAndClose = (result) => {
		setConfirmResult(result);
		setTimeout(() => {
			setModalOpen(false);
			setRefreshKey((prev) => prev + 1);
		}, 1500);
	};

	const handleConfirm = async () => {
		setConfirmLoading(true);
		setError(undefined);
		try {
			await api.post(`${uploadSoutUrl}/${draft.draft_id}/confirm`);
			showResultAndClose('confirmed');
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setConfirmLoading(false);
		}
	};

	const handleReject = async () => {
		setConfirmLoading(true);
		setError(undefined);
		try {
			await api.delete(`${uploadSoutUrl}/${draft.draft_id}`);
			showResultAndClose('rejected');
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		} finally {
			setConfirmLoading(false);
		}
	};

	const renderUploadBtnText = () => {
		if (loading) return <Spinner />;
		return 'Загрузить';
	};

	return (
		<div className={styles.wrap}>
			<h1 className={styles.title}>Загрузка результатов СОУТ</h1>
			<ErrorMessage error={error} onClose={() => setError(undefined)} />

			{phase === 'upload' && (
				<div className={styles.content}>
					<input
						ref={fileInputRef}
						type="file"
						accept=".sout"
						className={styles.hiddenInput}
						onChange={(e) => setFile(e.target.files[0] || null)}
					/>
					<button
						className={styles.fileBtn}
						onClick={() => fileInputRef.current.click()}
					>
						{file ? file.name : 'Выбрать файл .sout…'}
					</button>
					<p className={styles.hint}>
						Файл выгрузки из ФГИС СОУТ, полученный от аттестующей организации.
					</p>

					<div className={styles.buttons}>
						<Button onClick={handleUpload} disabled={!file || loading}>
							{renderUploadBtnText()}
						</Button>
					</div>
				</div>
			)}

			{phase === 'confirm' && draft && (
				<div className={styles.content}>
					<div className={styles.stats}>
						<div className={styles.statsRow}>
							<span>Новых профессий:</span>
							<strong>{draft.stats.professions_created} (в системе: {draft.stats.professions_existing})</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Новых подразделений:</span>
							<strong>{draft.stats.divisions_created} (в системе: {draft.stats.divisions_existing})</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Новых опасных факторов:</span>
							<strong>{draft.stats.activities_created}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Новых компенсаций:</span>
							<strong>{draft.stats.compensations_created}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Рабочих мест — новых:</span>
							<strong>{draft.stats.workplaces_created}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Рабочих мест — обновлено:</span>
							<strong>{draft.stats.workplaces_updated}</strong>
						</div>
						<div className={styles.statsRow}>
							<span>Рабочих мест — деактивировано:</span>
							<strong>{draft.stats.workplaces_deactivated}</strong>
						</div>
					</div>
					<p className={styles.hint}>Проверьте скачанный файл и подтвердите загрузку.</p>
					<div className={styles.buttons}>
						<Button
							onClick={handleConfirm}
							disabled={confirmLoading || confirmResult !== null}
							className={confirmResult === 'confirmed' ? styles.okBtn : ''}
						>
							{confirmResult === 'confirmed' ? '✓ Применено' : confirmLoading ? <Spinner /> : 'Применить'}
						</Button>
						<Button
							onClick={handleReject}
							disabled={confirmLoading || confirmResult !== null}
							className={confirmResult === 'rejected' ? styles.okBtn : ''}
						>
							{confirmResult === 'rejected' ? '✓ Отменено' : confirmLoading ? <Spinner /> : 'Отменить'}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}